import { isDestroyed, isDestroying } from '@ember/destroyable';
import { concat } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { local } from 'embroider-css-modules';
import type { RouterServiceWithLegacyTransitionTo } from 'lumen/initializers/router-legacy-transition-to';
import type AjaxService from 'lumen/services/ajax';
import type { NavAutocompleteResult } from 'lumen/services/hotel-and-market';
import { timeout } from 'lumen/utils/waiters';

import styles from './hotel-selection-dropdown.module.css';

const ROUTES = {
  HOTEL: {
    RATES: 'hotel.rates.index',
    ERROR: 'hotel.error',
    BUDGET: 'hotel.budget',
    BUDGET_DETAIL: 'hotel.settings.budget.detail',
    FORECAST: 'hotel.settings.forecast.detail',
    FORECAST_VIEW: 'hotel.forecast',
  },
  MARKET: {
    SMART_INSIGHTS: 'market.smart-insights',
    MARKET_RATES: 'market.marketrates.index',
    ERROR: 'market.error',
  },
  DESTINATION: {
    INDEX: 'destination.index',
    REPORTING_DASHBOARD: 'destination.reporting.dashboard',
  },
} as const;

const ICON_MAP = {
  hotel: 'business-type-hotel',
  market: 'business-type-market',
  destinationsubscription: 'location-pin',
} as const;

interface HotelAndMarketDropdownsHotelSelectionDropdownSignature {
  Args: {
    isDemoMode?: boolean;
  };
}

interface FormattedOption extends Option {
  destinationSubscriptionId?: string | null;
  hotelinfoId?: number;
  marketId?: string | null;
  subscriptionIds?: number[];
  type: 'hotel' | 'market' | 'destinationsubscription';
}

interface NavAutocompleteResponse {
  meta: {
    results_count: number;
    results_page: number;
    results_per_page: number;
  };
  results: NavAutocompleteResult[];
}

function isFormattedOption(
  option: FormattedOption | { label: string },
): option is FormattedOption {
  return 'type' in option && 'icon' in option;
}

export default class HotelAndMarketDropdownsHotelSelectionDropdown extends Component<HotelAndMarketDropdownsHotelSelectionDropdownSignature> {
  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service declare destination: Services['destination'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare router: RouterServiceWithLegacyTransitionTo;

  @tracked isOpen = false;
  @tracked options: FormattedOption[] = [];
  @tracked optionsFilter = '';
  @tracked page = 0;
  @tracked totalPages = 1;

  fetchOptionsTask = task(
    { restartable: true },
    async (): Promise<FormattedOption[]> => {
      const url = `${this.ajax.host}/apigateway/v1/app/users/me/accessible-entities/`;
      const data = {
        page: this.page,
        q: this.optionsFilter,
        market_id: this.hotelAndMarketService.market?.id,
      };

      try {
        const result = await this.ajax.request<NavAutocompleteResponse>(url, {
          method: 'GET',
          data,
        });

        if (!result.results.length) {
          return [];
        }

        // Filter out the current hotel/market/destination if we're viewing one
        const currentHotelinfoId =
          this.hotelAndMarketService.hotel?.hotelinfo?.id;
        const currentMarketId = this.hotelAndMarketService.market?.id;
        const currentDestinationId =
          this.destination.destinationSubscription?.id;

        const filteredResults = result.results.filter((autocompleteResult) => {
          // Filter out current hotel
          if (
            currentHotelinfoId &&
            autocompleteResult.hotelinfo_id === Number(currentHotelinfoId)
          ) {
            return false;
          }

          // Filter out current market
          if (
            currentMarketId &&
            autocompleteResult.market_id?.toString() === currentMarketId
          ) {
            return false;
          }

          // Filter out current destination subscription
          if (
            currentDestinationId &&
            autocompleteResult.destination_subscription_id?.toString() ===
              currentDestinationId
          ) {
            return false;
          }

          return true;
        });

        const options = this.formatNavAutocompleteResults(filteredResults);

        this.totalPages = Math.ceil(
          result.meta.results_count / result.meta.results_per_page,
        );

        return options;
      } catch (error) {
        this.errorTracking.captureException(error);
        return [];
      }
    },
  );

  loadMoreTask = task({ restartable: true }, async (): Promise<void> => {
    try {
      if (this.page + 1 < this.totalPages) {
        this.page += 1;
        const fetchedOptions = await this.fetchOptionsTask.perform();
        this.options = [...this.options, ...fetchedOptions];
      }
    } catch (error) {
      this.errorTracking.captureException(error);
    }
  });

  searchOptionTask = task(
    { restartable: true },
    async (optionsFilter: string): Promise<void> => {
      await timeout();

      this.optionsFilter = optionsFilter;
      this.page = 0;
      this.options = await this.fetchOptionsTask.perform();
    },
  );

  get selectedValue(): { label: string } | null {
    const hotel = this.hotelAndMarketService.hotel;
    if (hotel) {
      let selectedHotelName = '';

      if (this.args.isDemoMode) {
        selectedHotelName = hotel.hotelinfo.name ?? '';
      } else {
        selectedHotelName = hotel.hotelinfo.brandCode
          ? `${hotel.hotelinfo.brandCode} - ${hotel.hotelinfo.name}`
          : (hotel.hotelinfo.name ?? '');
      }

      return { label: selectedHotelName };
    }
    const market = this.hotelAndMarketService.market;
    if (market) {
      const marketDisplayName = market.displayName;

      // Append the user's email for staff users when looking at My Hotels
      if (
        this.hotelAndMarketService.isMyHotels &&
        this.access.isStaffUser &&
        market.ownerEmail &&
        !this.args.isDemoMode
      ) {
        return {
          label: `${marketDisplayName} (${market.ownerEmail})`,
        };
      }

      return { label: marketDisplayName };
    }
    const destinationSubscription = this.destination.destinationSubscription;
    if (destinationSubscription) {
      return { label: destinationSubscription.name };
    }

    return null;
  }

  formatNavAutocompleteResults(
    results: NavAutocompleteResult[],
  ): FormattedOption[] {
    // Format results using the hotel-and-market service for proper translation
    // and product sorting
    const formattedResults =
      this.hotelAndMarketService.formatNavAutocompleteResults(results, true);

    return formattedResults.map((result) => {
      const type =
        result.type === 'hotelinfo' ? 'hotel' : (result.type ?? 'hotel');
      const subLabelItems = result.plans?.length
        ? result.plans
        : result.products;

      return {
        label: result.name,
        unsafe_subLabel: subLabelItems?.join(', ') ?? '',
        icon: ICON_MAP[type],
        type,
        hotelinfoId: result.hotelinfo_id,
        marketId: result.market_id?.toString() ?? null,
        destinationSubscriptionId:
          result.destination_subscription_id?.toString() ?? null,
        subscriptionIds: result.subscription_ids,
      };
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleDestinationChange(
    id: string,
    currentRouteName: string,
    queryParams: Record<string, string | undefined>,
  ) {
    let targetRoute = currentRouteName;
    let finalQueryParams = { ...queryParams };

    // Check if we're in a destination route
    if (!currentRouteName.includes('destination.')) {
      finalQueryParams = {};
      targetRoute = ROUTES.DESTINATION.INDEX;
    }

    if (targetRoute === ROUTES.DESTINATION.REPORTING_DASHBOARD) {
      // if switching destination sub while in dashboard, go to main index
      return this.router.legacyTransitionTo(ROUTES.DESTINATION.INDEX, id);
    }

    return this.router.legacyTransitionTo(targetRoute, id, {
      queryParams: finalQueryParams,
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleHotelChange(
    id: string,
    currentRouteName: string,
    queryParams: Record<string, string | undefined>,
  ) {
    let targetRoute = currentRouteName;
    let finalQueryParams = { ...queryParams };

    // Check if we're in a hotel route
    if (!currentRouteName.includes('hotel.')) {
      finalQueryParams = {};
      targetRoute = ROUTES.HOTEL.RATES;
    }

    // Handle spider-dashboard route
    if (currentRouteName.includes('.spider-dashboard')) {
      const currentRoute = this.router.currentRoute;
      if (currentRoute) {
        const params = currentRoute.params as Record<string, string>;
        return this.router.transitionTo(currentRouteName, id, params['path'], {
          queryParams,
        });
      }
    }

    // Budget & Forecast detail routes - reset entry_id
    if (
      currentRouteName === ROUTES.HOTEL.BUDGET_DETAIL ||
      currentRouteName === ROUTES.HOTEL.FORECAST
    ) {
      return this.router.legacyTransitionTo(
        targetRoute,
        id,
        { entry_id: undefined },
        { queryParams: finalQueryParams },
      );
    }

    // Budget & Forecast view mode - reset entryId query param
    if (
      currentRouteName === ROUTES.HOTEL.BUDGET ||
      currentRouteName === ROUTES.HOTEL.FORECAST_VIEW
    ) {
      return this.router.legacyTransitionTo(targetRoute, id, {
        queryParams: { ...finalQueryParams, entryId: undefined },
      });
    }

    // Handle error routes
    if (
      currentRouteName === ROUTES.HOTEL.ERROR ||
      currentRouteName === ROUTES.MARKET.ERROR
    ) {
      return this.router.legacyTransitionTo(ROUTES.HOTEL.RATES, id, {
        queryParams: { reason: null },
      });
    }

    return this.router.legacyTransitionTo(targetRoute, id);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private handleMarketChange(
    id: string,
    currentRouteName: string,
    queryParams: Record<string, string | undefined>,
  ) {
    const defaultProductRoute = this.access.hasSmartInsights
      ? ROUTES.MARKET.SMART_INSIGHTS
      : ROUTES.MARKET.MARKET_RATES;

    let targetRoute = currentRouteName;
    let finalQueryParams = { ...queryParams };

    // Check if we're in a market route
    if (!currentRouteName.includes('market.')) {
      finalQueryParams = {};
      targetRoute = defaultProductRoute;
    }

    // Reset page parameter on market switch
    if (finalQueryParams['page']) {
      finalQueryParams['page'] = '1';
    }

    // Handle spider-dashboard route
    if (currentRouteName.includes('.spider-dashboard')) {
      const currentRoute = this.router.currentRoute;
      if (currentRoute) {
        const params = currentRoute.params as Record<string, string>;
        return this.router.transitionTo(currentRouteName, id, params['path'], {
          queryParams,
        });
      }
    }

    // Handle error routes
    if (
      currentRouteName === ROUTES.HOTEL.ERROR ||
      currentRouteName === ROUTES.MARKET.ERROR
    ) {
      return this.router.legacyTransitionTo(defaultProductRoute, id, {
        queryParams: { reason: null },
      });
    }

    return this.router.legacyTransitionTo(targetRoute, id, {
      queryParams: finalQueryParams,
    });
  }

  resetValues(): void {
    this.optionsFilter = '';
    this.page = 0;
    this.options = [];
  }

  @action handleChange(option: FormattedOption | { label: string }): void {
    if (!isFormattedOption(option)) {
      return;
    }

    const { type } = option;

    // Determine the ID based on the type
    const id =
      option.marketId ||
      option.destinationSubscriptionId ||
      option.subscriptionIds?.[0]?.toString();

    if (!id) {
      return;
    }

    const currentRoute = this.router.currentRoute;
    const currentRouteName = this.router.currentRouteName ?? '';
    const currentQueryParams = (currentRoute?.queryParams ?? {}) as Record<
      string,
      string | undefined
    >;

    if (type === 'market') {
      this.handleMarketChange(id, currentRouteName, currentQueryParams);
    } else if (type === 'hotel') {
      this.handleHotelChange(id, currentRouteName, currentQueryParams);
    } else if (type === 'destinationsubscription') {
      this.handleDestinationChange(id, currentRouteName, currentQueryParams);
    }
  }

  @action toggleDropdown(): void {
    if (isDestroyed(this) || isDestroying(this)) {
      return;
    }

    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      void this.searchOptionTask.perform(this.optionsFilter);
    } else {
      this.resetValues();
    }
  }

  <template>
    <div
      class={{local styles "dropdown" (if this.isOpen "dropdown-full-width")}}
    >
      <PrismComboSelectInput
        @disabled={{@isDemoMode}}
        @horizontalPosition="right"
        @isSearchable={{true}}
        @loadingMessage={{concat (t "general.loading") "..."}}
        @onChange={{this.handleChange}}
        @onClose={{this.toggleDropdown}}
        @onOpen={{this.toggleDropdown}}
        @onSearch={{this.searchOptionTask.perform}}
        @options={{this.options}}
        @unsafe_dropdownClass="hotel-selection-dropdown__content"
        @unsafe_isClampedToOneLine={{true}}
        @unsafe_onLoadMore={{this.loadMoreTask.perform}}
        @value={{this.selectedValue}}
        data-test-id="hotel-selection-dropdown"
      />
    </div>
  </template>
}
